import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './index.css';
import { useEffect, useState } from 'react';
import { useAuth } from './context/AuthContext';
import { useProfile } from './hooks/useProfile';
import { supabase } from './services/supabase';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import FundProfile from './pages/FundProfile';
import FirmProfile from './pages/FirmProfile';
import Pipeline from './pages/Pipeline';
import Portfolio from './pages/Portfolio';
import Messages from './pages/Messages';
import CreateFund from './pages/CreateFund';
import UpdatePerformance from './pages/UpdatePerformance';
import ManageInvestors from './pages/ManageInvestors';
import Calendar from './pages/Calendar';
import Compare from './pages/Compare';
import ManageFunds from './pages/ManageFunds';
import AltConnect from './pages/AltConnect';
import ManageTeam from './pages/ManageTeam';
import AltConnectAI from './pages/AltConnectAI';
import MarketIntelligence from './pages/MarketIntelligence';
import PeerAnalysis from './pages/PeerAnalysis';
import PeerAnalysisSelector from './pages/PeerAnalysisSelector';
import AllocatorProfile from './pages/AllocatorProfile';
import ManageDDQ from './pages/ManageDDQ';
import DDQWizard from './pages/DDQWizard';
import DDQAccess from './pages/DDQAccess';
import DDQView from './pages/DDQView';
import AllocatorDDQ from './pages/AllocatorDDQ';
import DataRoom from './pages/DataRoom';
import Admin from './pages/Admin';
import InviteLanding from './pages/InviteLanding';
import ProfileSettings from './pages/ProfileSettings';
import PendingReview from './pages/PendingReview';
import ServiceConnect from './pages/ServiceConnect';
import ServiceProviderProfile from './pages/ServiceProviderProfile';
import ServiceProviderDashboard from './pages/ServiceProviderDashboard';
import ServiceProviderOnboarding from './pages/ServiceProviderOnboarding';
// EventConnect / PulseConnect landing pages are parked behind RetiredRoute; the
// admin + deep-link sub-pages below stay routed.
import EventConnectSetup from './pages/EventConnectSetup';
import EventConnectInvestor from './pages/EventConnectInvestor';
import PBEventAdmin from './pages/PBEventAdmin';
import TalentConnect from './pages/TalentConnect';
import CandidateProfile from './pages/CandidateProfile';
import CandidateDashboard from './pages/CandidateDashboard';
import CandidateOnboarding from './pages/CandidateOnboarding';
import CandidateJobBoard from './pages/CandidateJobBoard';
import PBTalentAdmin from './pages/PBTalentAdmin';
import SurveyTaker from './pages/SurveyTaker';
import SurveyResults from './pages/SurveyResults';
import PBSurveyAdmin from './pages/PBSurveyAdmin';
import IntelligenceLayer from './pages/IntelligenceLayer';
import Onboarding from './pages/Onboarding';
import Apply from './pages/Apply';
import ApplicationStatus from './pages/ApplicationStatus';
import DocumentViewer from './pages/DocumentViewer';
import ApplyLanding from './pages/ApplyLanding';
import ApplyAllocator from './pages/ApplyAllocator';
import ApplyProvider from './pages/ApplyProvider';
import ApplyCandidate from './pages/ApplyCandidate';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';

// Layout
import AppLayout from './components/layout/AppLayout';

const loadingScreen = (
  <div style={{
    minHeight: '100vh', background: '#F5F6F8',
    display: 'flex', alignItems: 'center', justifyContent: 'center'
  }}>
    <div style={{ color: '#6B7280', fontSize: '14px' }}>Loading...</div>
  </div>
);

function ProtectedRoute({ children }: { children: React.ReactElement }) {
  const { user, loading: authLoading } = useAuth();
  const { profile, loading: profileLoading } = useProfile();

  if (authLoading || (user && profileLoading)) return loadingScreen;
  if (!user) return <Navigate to="/login" replace />;

  // Non-approved users get sent to the pending screen
  if (profile && profile.account_status !== 'approved') {
    return <Navigate to="/pending" replace />;
  }

  return children;
}

// Applicants are not approved yet, so they must not be bounced to /pending the
// way ProtectedRoute does, they need their own holding portal.
function ApplicantRoute({ children }: { children: React.ReactElement }) {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  if (authLoading) return loadingScreen;
  // Carry where they were headed through the login round trip: these links
  // arrive from email, so the reader is often signed out.
  if (!user) {
    const back = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/login?redirect=${back}`} replace />;
  }
  return children;
}

function PendingRoute({ children }: { children: React.ReactElement }) {
  const { user, loading: authLoading } = useAuth();
  const { profile, loading: profileLoading } = useProfile();
  const [hasApplication, setHasApplication] = useState<boolean | null>(null);

  // A pending user who came through /apply belongs in the application portal
  // rather than the generic "awaiting review" screen.
  useEffect(() => {
    if (!user) { setHasApplication(false); return; }
    let cancelled = false;
    supabase.from('manager_applications').select('status')
      .order('created_at', { ascending: false }).limit(1).maybeSingle()
      .then(({ data }) => {
        if (cancelled) return;
        // 'contract_signed' still counts as in-flight: the applicant has signed
        // but an admin has not activated the account yet.
        setHasApplication(!!data && (data as any).status !== 'approved');
      });
    return () => { cancelled = true; };
  }, [user]);

  if (authLoading || (user && profileLoading) || hasApplication === null) return loadingScreen;
  if (!user) return <Navigate to="/login" replace />;

  // Approved users get sent to dashboard
  if (profile && profile.account_status === 'approved') {
    return <Navigate to="/dashboard" replace />;
  }

  if (hasApplication) return <Navigate to="/application-status" replace />;

  return children;
}

// Decides where an authenticated, approved user lands. Service providers are
// routed to onboarding (no provider profile yet) or their dashboard.
function HomeRedirect() {
  const { profile, loading } = useProfile();
  const [dest, setDest] = useState<string | null>(null);

  useEffect(() => {
    if (loading) return;
    if (!profile) { setDest('/dashboard'); return; }
    if (profile.role === 'service_provider') {
      let active = true;
      supabase
        .from('service_providers')
        .select('id')
        .eq('owner_id', profile.id)
        .maybeSingle()
        .then(({ data }) => {
          if (active) setDest(data ? '/service-provider-dashboard' : '/service-provider-onboarding');
        });
      return () => { active = false; };
    }
    if (profile.role === 'candidate') {
      let active = true;
      supabase
        .from('candidates')
        .select('id')
        .eq('owner_id', profile.id)
        .maybeSingle()
        .then(({ data }) => {
          if (active) setDest(data ? '/candidate-dashboard' : '/candidate-onboarding');
        });
      return () => { active = false; };
    }
    setDest('/dashboard');
  }, [profile, loading]);

  if (!dest) return loadingScreen;
  return <Navigate to={dest} replace />;
}

// Products that are not part of the direct manager/allocator platform yet.
// Users land back on the dashboard with an optional notice.
function RetiredRoute({ notice }: { notice?: string }) {
  return <Navigate to="/dashboard" replace state={notice ? { notice } : undefined} />;
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/invite/:token" element={<InviteLanding />} />
        {/* Public, anyone can apply without an account. */}
        <Route path="/apply" element={<ApplyLanding />} />
        <Route path="/apply/manager" element={<Apply />} />
        <Route path="/apply/allocator" element={<ApplyAllocator />} />
        <Route path="/apply/provider" element={<ApplyProvider />} />
        <Route path="/apply/candidate" element={<ApplyCandidate />} />
        {/* Legal documents: public, readable signed out and linked from email. */}
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/application-status" element={
          <ApplicantRoute>
            <ApplicationStatus />
          </ApplicantRoute>
        } />
        {/* Outside AppLayout: the document fills the viewport, no sidebar. Uses
            ApplicantRoute because an applicant reading their agreement is not
            an approved account yet, so ProtectedRoute would bounce them. */}
        <Route path="/documents/:applicationId/:docType" element={
          <ApplicantRoute>
            <DocumentViewer />
          </ApplicantRoute>
        } />
        <Route path="/pending" element={
          <PendingRoute>
            <PendingReview />
          </PendingRoute>
        } />
        <Route path="/onboarding" element={
          <ProtectedRoute>
            <Onboarding />
          </ProtectedRoute>
        } />
        <Route path="/" element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }>
          <Route index element={<HomeRedirect />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="alt-connect" element={<AltConnect />} />
          <Route path="alt-connect/firm/:id" element={<FirmProfile />} />
          <Route path="alt-connect/fund/:id" element={<FundProfile />} />
          <Route path="pipeline" element={<Pipeline />} />
          <Route path="portfolio" element={<Portfolio />} />
          <Route path="messages" element={<Messages />} />
          <Route path="create-fund" element={<CreateFund />} />
          <Route path="update-performance" element={<UpdatePerformance />} />
          <Route path="manage-investors" element={<ManageInvestors />} />
          <Route path="calendar" element={<Calendar />} />
          <Route path="compare" element={<Compare />} />
          <Route path="manage-funds" element={<ManageFunds />} />
          <Route path="manage-team" element={<ManageTeam />} />
          <Route path="alt-connect-ai" element={<AltConnectAI />} />
          <Route path="peer-analysis/:fundId" element={<PeerAnalysis />} />
          <Route path="peer-analysis" element={<PeerAnalysisSelector />} />
          <Route path="market-intelligence" element={<MarketIntelligence />} />
          <Route path="allocator-profile/:connectionId" element={<AllocatorProfile />} />
          <Route path="manage-ddq" element={<ManageDDQ />} />
          <Route path="ddq/new" element={<DDQWizard />} />
          <Route path="ddq/:ddqId/access" element={<DDQAccess />} />
          <Route path="ddq/view/:ddqId" element={<DDQView />} />
          <Route path="ddq-library" element={<AllocatorDDQ />} />
          <Route path="data-room" element={<DataRoom />} />
          <Route path="service-connect" element={<ServiceConnect />} />
          <Route path="service-connect/provider/:id" element={<ServiceProviderProfile />} />
          <Route path="service-provider-dashboard" element={<ServiceProviderDashboard />} />
          <Route path="service-provider-onboarding" element={<ServiceProviderOnboarding />} />
          <Route path="event-connect" element={<RetiredRoute notice="EventConnect is coming soon" />} />
          <Route path="event-connect/setup/:eventId" element={<EventConnectSetup />} />
          <Route path="event-connect/preferences/:eventId" element={<EventConnectInvestor />} />
          <Route path="pb-event-admin" element={<PBEventAdmin />} />
          <Route path="talent-connect" element={<TalentConnect />} />
          <Route path="talent-connect/candidate/:id" element={<CandidateProfile />} />
          <Route path="candidate-dashboard" element={<CandidateDashboard />} />
          <Route path="candidate-onboarding" element={<CandidateOnboarding />} />
          <Route path="jobs" element={<CandidateJobBoard />} />
          <Route path="pb-talent-admin" element={<PBTalentAdmin />} />
          <Route path="pulse-connect" element={<RetiredRoute />} />
          <Route path="pulse-connect/survey/:id" element={<SurveyTaker />} />
          <Route path="pulse-connect/results/:id" element={<SurveyResults />} />
          <Route path="pb-survey-admin" element={<PBSurveyAdmin />} />
          <Route path="intelligence" element={<IntelligenceLayer />} />
          <Route path="profile-settings" element={<ProfileSettings />} />
          <Route path="admin" element={<Admin />} />
        </Route>
      </Routes>
    </Router>
  );
}

export default App;